<template>
  <div class="captcha-container w-full bg-gray-50 rounded-lg p-4 mb-4 select-none border border-gray-200">
    <p class="text-xs text-gray-500 mb-2 font-bold uppercase tracking-wider">{{ hint }}</p>

    <div ref="track" class="relative h-[150px] bg-white border border-gray-300 rounded-md overflow-hidden shadow-inner">

      <img :src="bbg" alt="background" class="w-full h-full object-fill pointer-events-none" />

      <img :src="sslice" class="absolute z-20 cursor-pointer drop-shadow-lg" :style="{
        left: sliderX + 'px',
        top: backendY + 'px',
        height: (pieceFullSize / scale) + 'px'
      }" />

      <div class="absolute left-0 top-0 h-full bg-blue-50/30" :style="{ width: sliderX + 'px' }"></div>
      <div
        class="absolute top-0 h-full w-12 flex items-center justify-center cursor-grab active:cursor-grabbing z-30 shadow-lg text-white"
        :style="{ transform: `translateX(${sliderX}px)` }" @mousedown="startDrag" @touchstart.passive="startDrag">
        <span class="font-bold">{{ verified ? '✓' : '≫' }}</span>
      </div>

      <div v-if="!verified"
        class="absolute inset-0 flex items-center justify-center text-sm text-gray-400 pointer-events-none">
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted } from 'vue'
import { useLogin } from './Login'
import { logger } from '@myorg/type-lib';

const props = defineProps({
  label: { type: String, default: 'Verification' },
  hint: { type: String, default: 'Slide to the target' }
})

const emit = defineEmits(['verified', 'failed'])
const { initCaptcha } = useLogin()

// 狀態變數
const track = ref<HTMLElement | null>(null)
const sliderX = ref(0)
const captchaId = ref('')
const bbg = ref('')
const sslice = ref('')
const backendY = ref(0)
const scale = ref(1)
const pieceFullSize = ref(74) // 根據後端 PieceSize + Radius*2

const isDragging = ref(false)
const verified = ref(false)
const dragPath = ref<{ x: number, t: number }[]>([])

const getBase64ImageSize = (base64: string): Promise<{ width: number, height: number }> => {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      resolve({
        width: img.naturalWidth,
        height: img.naturalHeight
      });
    };
    img.src = base64;
  });
};

const initTarget = async () => {
  const res = await initCaptcha()
  if (res && track.value) {
    const { captchaId: id, target, bg, slice } = res; // 注意這裡從 res.data 解構
    captchaId.value = id;

    const size = await getBase64ImageSize(bg);
    bbg.value = bg
    sslice.value = slice
    const y = res.y || 0

    // 正確的比例計算：前端容器寬度 / 後端圖片原始寬度
    // 假設後端固定輸出 600px
    // 修正：計算 1 個前端像素等於多少後端像素
    // 範例：後端 600px / 前端 300px = 2 (倍率)
    scale.value = size.width / track.value.clientWidth
    const trackHeight = track.value.clientHeight
    backendY.value = (y / 100) * trackHeight
  }
}

const startDrag = () => {
  if (verified.value) return
  isDragging.value = true
  dragPath.value = []
}

const handleMove = (e: MouseEvent | TouchEvent) => {
  if (!isDragging.value || !track.value) return
  const rect = track.value.getBoundingClientRect()
  const clientX = 'touches' in e ? e.touches[0].clientX : (e as MouseEvent).clientX
  let x = clientX - rect.left - 24

  const maxX = rect.width - 48
  sliderX.value = Math.max(0, Math.min(x, maxX))
  dragPath.value.push({ x: Math.round(x), t: Date.now() })
  if (dragPath.value.length > 100) dragPath.value.shift()

}

const stopDrag = () => {
  if (!isDragging.value) return
  isDragging.value = false

  // 1. 計算最大可滑動寬度 (軌道寬度 - 滑塊按鈕寬度)
  // 假設你的滑塊按鈕寬度是 48px (w-12)
  const handleWidth = 48
  const maxX = track.value.clientWidth - handleWidth

  // Calculate percentage
  let percentage = (sliderX.value / maxX) * 100

  // CALIBRATION: 
  // Your Go code uses targetX (left edge). 
  // Your JS uses sliderX (handle center).
  // This ~10% adjustment aligns the "visual center" of the slider 
  // with the "left edge" of the puzzle piece.
  const CALIBRATION = 2
  const finalUserX = Math.round(percentage - CALIBRATION)
  logger.log("finalUserX",finalUserX)

  completeVerification(finalUserX)
}

const completeVerification = (finalX: number) => {
  // 將所有「原始數據」傳給後端
  // 後端會根據 captchaId 去 Redis 查真正的 targetX
  emit('verified', {
    targetIndex: captchaId.value,

    finalRatio: finalX,
    path: dragPath.value.map(p => ({
      x: Math.round(p.x / scale.value),
      t: p.t
    }))
  })
}

const reset = () => {
  sliderX.value = 0
  verified.value = false
  initTarget()
}

defineExpose({ reset, setVerified: (val: boolean) => verified.value = val })

onMounted(() => {
  initTarget()
  window.addEventListener('mousemove', handleMove)
  window.addEventListener('touchmove', handleMove, { passive: false })
  window.addEventListener('mouseup', stopDrag)
  window.addEventListener('touchend', stopDrag)
})
</script>